<template>
    <div class="mail-thread-container" :style="{ width: '100%' }">
        <!-- Başlık Bölümü -->
        <thread-header
            :ref-visitor-id="thread.refVisitor"
            :subject="thread.subject"
            @delete-thread="handleDeleteThread"
            @close="$emit('close')"
            @reply="handleHeaderReply"
            @reply-all="handleHeaderReplyAll"
            @forward="handleHeaderForward"
            :threadMails="threadMails"
        />

        <!-- Scroll Edilebilir İçerik Alanı -->
        <div class="scrollable-container" ref="scrollContainer">
            <!-- Thread İçeriği -->
            <div class="thread-messages">
                <div v-if="!threadMails.length" class="loading-thread">
                    <p>Mail thread içeriği yükleniyor...</p>
                </div>

                <!-- İlk mesaj her zaman gösterilir -->
                <div v-if="threadMails.length > 0" class="message-container">
                    <thread-message
                        :mail="threadMails[0]"
                        :key="threadMails[0]._id"
                        :defaultExpanded="threadMails.length === 1"
                        :threadShowsComposeForm="showReply && selectedMailId === threadMails[0]._id"
                        @reply="handleReply"
                        @forward="handleForward"
                        @delete="handleDelete"
                        @open-compose="handleMessageOpenCompose"
                    />

                    <!-- İlk maile yanıt formu -->
                    <mail-compose
                        v-if="showReply && selectedMailId === threadMails[0]._id"
                        :mailData="replyMailData"
                        :isReply="!isForward"
                        :isForward="isForward"
                        :showHeader="false"
                        @close="closeReplyForm"
                        @sent="handleSent"
                        class="reply-compose-form"
                        :selected-email-id="selectedEmailId"
                        :threadMails="threadMails"
                    />
                </div>

                <!-- Ortadaki mesajlar sıkıştırılmış olarak gösterilir, collapse butonu olmadan -->
                <div
                    v-if="threadMails.length > 2 && !showAllMessages"
                    class="collapsed-messages"
                    @click="expandAllMessages"
                >
                    <div class="collapsed-messages-icon">
                        <i class="material-icons">expand_more</i>
                    </div>
                    <div class="collapsed-messages-text">{{ threadMails.length - 2 }} adet daha mail göster...</div>
                </div>

                <!-- Açılan orta mesajlar (sıkıştırma butonu yok) -->
                <template v-if="showAllMessages && threadMails.length > 2">
                    <div
                        v-for="mail in threadMails.slice(1, threadMails.length - 1)"
                        :key="mail._id"
                        class="message-container"
                    >
                        <thread-message
                            :mail="mail"
                            :defaultExpanded="false"
                            :threadShowsComposeForm="showReply && selectedMailId === mail._id"
                            @reply="handleReply"
                            @forward="handleForward"
                            @delete="handleDelete"
                            @open-compose="handleMessageOpenCompose"
                        />

                        <!-- Orta maile yanıt formu -->
                        <mail-compose
                            v-if="showReply && selectedMailId === mail._id"
                            :mailData="replyMailData"
                            :isReply="!isForward"
                            :isForward="isForward"
                            :showHeader="false"
                            @close="closeReplyForm"
                            @sent="handleSent"
                            class="reply-compose-form"
                            :threadMails="threadMails"
                            :selected-email-id="selectedEmailId"
                        />
                    </div>
                </template>

                <!-- Son mesaj her zaman açık olarak gösterilir (birden fazla mesaj varsa) -->
                <div v-if="threadMails.length > 1" class="message-container">
                    <thread-message
                        :mail="threadMails[threadMails.length - 1]"
                        :key="threadMails[threadMails.length - 1]._id"
                        :defaultExpanded="true"
                        :threadShowsComposeForm="
                            showReply && selectedMailId === threadMails[threadMails.length - 1]._id
                        "
                        @reply="handleReply"
                        @forward="handleForward"
                        @delete="handleDelete"
                        @open-compose="handleMessageOpenCompose"
                    />

                    <!-- Son maile yanıt formu -->
                    <mail-compose
                        v-if="showReply && selectedMailId === threadMails[threadMails.length - 1]._id"
                        :mailData="replyMailData"
                        :isReply="!isForward"
                        :isForward="isForward"
                        :showHeader="false"
                        @close="closeReplyForm"
                        @sent="handleSent"
                        class="reply-compose-form"
                        :threadMails="threadMails"
                        :selected-email-id="selectedEmailId"
                    />
                </div>

                <!-- Alt taraftaki yanıtlama butonları -->
                <div class="thread-footer-actions" v-if="threadMails.length > 0 && !showReply">
                    <div class="action-buttons">
                        <SupsisButton
                            @click="handleReply(threadMails[threadMails.length - 1])"
                            icon="reply"
                            class="action-btn"
                            primary
                        >
                            Yanıtla
                        </SupsisButton>

                        <SupsisButton
                            @click="handleReplyAll(threadMails[threadMails.length - 1])"
                            icon="reply_all"
                            class="action-btn"
                            primary
                            v-if="hasMultipleRecipients(threadMails[threadMails.length - 1])"
                        >
                            Tümünü Yanıtla
                        </SupsisButton>

                        <SupsisButton
                            @click="handleForward(threadMails[threadMails.length - 1])"
                            icon="forward"
                            class="action-btn"
                            primary
                        >
                            Yönlendir
                        </SupsisButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ThreadHeader from "./ThreadHeader.vue";
import ThreadMessage from "./ThreadMessage.vue";
import MailCompose from "./MailCompose.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { mapActions, mapState } from "vuex";
import { MAIL_API } from "../../../../../api/mail";
import _ from "lodash";

export default {
    name: "MailThread",
    components: {
        ThreadHeader,
        ThreadMessage,
        MailCompose,
        SupsisButton,
    },
    props: {
        thread: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            threadMails: [],
            cancels: [], // İnterval ve listener referansları için dizi
            showReply: false,
            replyMailData: {},
            showAllMessages: false, // Tüm mesajları göster/gizle durumu
            isForward: false, // Yönlendirme işlemi için ekstra durum
            selectedMailId: "",
        };
    },
    computed: {
        ...mapState({
            emailSendSuccess: (state) => state.mail.emailSendSuccess,
        }),
        selectedEmail() {
            return this.$parent.selectedEmail;
        },
        selectedEmailId() {
            return this.selectedEmail._id;
        },
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        emailSendSuccess(val) {
            if (val) this.mailSuccessDelivered();
        },
        thread: {
            immediate: true,
            deep: true,
            handler() {
                this.fetchThreadMails();
            },
        },
    },
    mounted() {
        // Thread yüklendikten sonra en alta scroll yap
        this.$nextTick(() => {
            this.scrollToBottom();
        });
    },
    methods: {
        ...mapActions({
            getMailDetails: "mail/getMailDetails",
        }),
        mailSuccessDelivered: _.debounce(function () {
            this.fetchThreadMails();
        }, 1000),
        fetchThreadMails() {
            const threadId = this.thread.threadId;
            console.log("fetchThreadMails", threadId);
            this.getMailDetails(threadId).then((mails) => {
                this.threadMails = mails;
                // Mailler yüklendikten sonra en alta scroll yap
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            });
        },
        expandAllMessages() {
            this.showAllMessages = true;
        },
        handleMessageOpenCompose(data) {
            if (this.showReply) {
                this.closeReplyForm();
            }

            if (data.type === "reply") {
                this.handleReply(data.mail);
            } else if (data.type === "forward") {
                this.handleForward(data.mail);
            } else if (data.type === "replyAll") {
                this.handleReplyAll(data.mail);
            }
        },
        handleSent(mail) {
            this.fetchThreadMails();
            this.closeReplyForm();
        },
        handleReply(mail) {
            if (this.showReply) {
                this.closeReplyForm();
            }

            // Yanıtlama işlemi - doğrudan MailCompose bileşenini göster
            this.replyMailData = this.prepareReplyData(mail);
            this.isForward = false;
            this.showReply = true;
            this.selectedMailId = mail._id;

            // Form gösterildikten sonra sayfayı en alta kaydır
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        handleForward(mail) {
            // Eğer mevcut bir form açıksa kapat
            if (this.showReply) {
                this.closeReplyForm();
            }

            // MailCompose bileşenini thread içinde göster
            this.replyMailData = this.prepareForwardData(mail);
            this.isForward = true;
            this.showReply = true;
            this.selectedMailId = mail._id;

            // Form gösterildikten sonra sayfayı en alta kaydır
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        handleDelete(mail) {
            this.$dialog
                .confirm("Bu mail silinecektir. Devam etmek istiyor musunuz?", {
                    okText: "Evet",
                    cancelText: "Hayır",
                    customClass: "supsis-dialog",
                })
                .then(() => {
                    MAIL_API.deleteThreadMessage(this.thread._id, mail._id)
                        .then((response) => {
                            const { threadDeleted, mailDeleted } = response;
                            if (threadDeleted) this.$emit("thread-deleted");
                            if (!threadDeleted) this.fetchThreadMails();
                        })
                        .catch((error) => {
                            console.error("Mail silinirken hata:", error);
                            this.$message.error("Mail silinirken bir hata oluştu");
                        });
                });
        },
        handleDeleteThread() {
            this.$dialog
                .confirm("Bu mail zinciri silinecektir. Devam etmek istiyor musunuz?", {
                    okText: "Evet",
                    cancelText: "Hayır",
                    customClass: "supsis-dialog",
                })
                .then(() => {
                    MAIL_API.deleteThread(this.thread._id)
                        .then(() => {
                            this.$emit("thread-deleted");
                            this.$message.success("Mail thread başarıyla silindi");
                        })
                        .catch((error) => {
                            console.error("Mail thread silinirken hata:", error);
                            this.$message.error("Mail thread silinirken bir hata oluştu");
                        });
                });
        },
        prepareReplyData(mail) {
            const subject = mail.subject;

            // Kime yanıt verileceğini belirle
            let toAddress = "";

            // Maili gönderen kişi kendimizse (kendi gönderdiğimiz maili yanıtlıyorsak)
            if (mail.sender === this.selectedEmail.mail) {
                // İlk alıcıya yanıt ver
                toAddress = mail.to_list && mail.to_list.length > 0 ? mail.to_list[0] : "";
            } else {
                // Normal durumda gönderene yanıt ver
                toAddress = mail.sender;
            }

            const replyHeader = `
                <div>
                    <p>---------- Yanıtlanan mesaj ---------</p>
                </div>
            `;

            const body = `
                <br/><br/><br/><br/><br/><br/><br/><br/>
                <collapsed-mail>
                    ${replyHeader}
                    <div>${mail.body_html || ""}</div>
                    <hr/>
                </collapsed-mail>
            `;

            return {
                subject: subject,
                to: toAddress,
                cc: "",
                bcc: "",
                body: body,
                attachments: [],
                threadId: this.thread.threadId,
                messageId: mail.messageId,
            };
        },
        prepareForwardData(mail) {
            // Yönlendirme için mail verisi hazırla
            const subject = mail.subject.startsWith("Fwd:") ? mail.subject : `Fwd: ${mail.subject}`;

            // Temel bilgileri düz metin olarak hazırlayalım
            const forwardHeader = `
                <div>
                  <p>---------- Yönlendirilen mesaj ---------</p>
                  <p>Kimden: ${mail.sender}</p>
                  <p>Tarih: ${this.$moment(mail.createdAt).format("DD MMMM YYYY, HH:mm")}</p>
                  <p>Konu: ${mail.subject}</p>
                  <p>Kime: ${mail.to_list.join(", ")}</p>
                </div>
            `;

            // Mail içeriği
            // div ile sarmalayarak block element oluşturuyoruz
            const mailContent = `<div>${mail.body_html || ""}</div>`;

            // Forward etiketini kullanırken, içinde block içerik olacak şekilde ayarlıyoruz
            const forwardBody = `
                <br/><br/><br/><br/><br/><br/><br/><br/>
                <collapsed-mail>
                    <hr/>
                    ${forwardHeader}
                    ${mailContent}
                    <hr/>
                </collapsed-mail>
            `;

            return {
                subject: subject,
                to: "",
                cc: "",
                bcc: "",
                body: forwardBody,
                attachments: [],
                threadId: this.thread.threadId,
                messageId: mail.messageId,
            };
        },
        closeReplyForm() {
            this.showReply = false;
            this.isForward = false;
        },
        scrollToBottom() {
            const scrollContainer = this.$refs.scrollContainer;
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        },
        hasMultipleRecipients(mail) {
            const allRecipients = [...(mail.to_list || []), ...(mail.cc_list || [])].filter(
                (email) => email !== this.selectedEmail.mail
            );
            return allRecipients.length > 1;
        },
        handleReplyAll(mail) {
            if (this.showReply) {
                this.closeReplyForm();
            }

            // Tümünü Yanıtla için mail verisi hazırla
            this.replyMailData = this.prepareReplyAllData(mail);
            this.isForward = false;
            this.showReply = true;
            this.selectedMailId = mail._id;

            // Form gösterildikten sonra sayfayı en alta kaydır
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        prepareReplyAllData(mail) {
            const subject = mail.subject;

            let toList = [];
            let ccList = [];

            // Maili gönderen kişi kendimizse (kendi gönderdiğimiz maili yanıtlıyorsak)
            if (mail.sender === this.selectedEmail.mail) {
                // TO listesinden ilk kişiyi TO olarak al, diğerlerini CC'ye ekle
                if (mail.to_list && mail.to_list.length > 0) {
                    toList = [mail.to_list[0]]; // İlk alıcı
                    // Kalan alıcıları CC'ye ekle (eğer varsa) - kendimizi hariç tut
                    if (mail.to_list.length > 1) {
                        ccList = [...mail.to_list.slice(1).filter((email) => email !== this.selectedEmail.mail)];
                    }
                }

                // Varsa mevcut CC listesini de ekle - kendimizi hariç tut
                if (mail.cc_list && mail.cc_list.length > 0) {
                    ccList = [...ccList, ...mail.cc_list.filter((email) => email !== this.selectedEmail.mail)];
                }
            }
            // Maili bize başkası göndermişse (normal durum)
            else {
                // Sadece göndereni "to" alanına koy
                toList = [mail.sender];

                // Kendi e-posta adresimizi çıkararak diğer alıcıları CC'ye ekle
                const otherRecipients = mail.to_list.filter(
                    (email) => email !== this.selectedEmail.mail && email !== mail.sender
                );

                // Mevcut CC listesi ile diğer alıcıları birleştir - kendimizi hariç tut
                ccList = [...otherRecipients];
                if (mail.cc_list && mail.cc_list.length > 0) {
                    const ccRecipients = mail.cc_list.filter((email) => email !== this.selectedEmail.mail);
                    ccList = [...ccList, ...ccRecipients];
                }
            }

            const replyHeader = `
                <div>
                    <p>---------- Yanıtlanan mesaj ---------</p>
                </div>
            `;

            const body = `
                <br/><br/><br/><br/><br/><br/><br/><br/>
                <collapsed-mail>
                    ${replyHeader}
                    <div>${mail.body_html || ""}</div>
                    <hr/>
                </collapsed-mail>
            `;

            return {
                subject: subject,
                to: toList.join(", "),
                cc: ccList.join(", "),
                bcc: "",
                body: body,
                attachments: [],
                threadId: this.thread.threadId,
                messageId: mail.messageId,
            };
        },
        // Header'dan gelen işlem isteklerini ele alacak metodlar
        handleHeaderReply() {
            // Thread'in son mesajını kullanarak yanıtla
            if (this.threadMails.length > 0) {
                this.handleReply(this.threadMails[this.threadMails.length - 1]);
            }
        },
        handleHeaderReplyAll() {
            // Thread'in son mesajını kullanarak tümünü yanıtla
            if (this.threadMails.length > 0) {
                this.handleReplyAll(this.threadMails[this.threadMails.length - 1]);
            }
        },
        handleHeaderForward() {
            // Thread'in son mesajını kullanarak yönlendir
            if (this.threadMails.length > 0) {
                this.handleForward(this.threadMails[this.threadMails.length - 1]);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.mail-thread-container {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    height: 100%;
    overflow: hidden;

    .scrollable-container {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;

        /* Özel scrollbar stilleri */
        &::-webkit-scrollbar {
            width: 5px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
            background: #22272d;
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #22272d;
        }

        /* Firefox için scrollbar stilleri */
        scrollbar-width: thin;
        scrollbar-color: #22272d #f1f1f1;
    }

    .thread-messages {
        padding: 16px;

        .message-container {
            margin-bottom: 16px;
        }

        .loading-thread {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100px;
            color: #666;
        }

        .collapsed-messages {
            display: flex;
            align-items: center;
            background-color: #f5f5f5;
            border-radius: 8px;
            padding: 12px 16px;
            margin: 16px 0;
            cursor: pointer;
            transition: background-color 0.2s;

            &:hover {
                background-color: #eaeaea;
            }

            .collapsed-messages-icon {
                color: #22272d;
                margin-right: 8px;

                i {
                    font-size: 24px;
                }
            }

            .collapsed-messages-text {
                color: #444;
                font-weight: 500;
                font-size: 14px;
            }
        }

        .thread-footer-actions {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #eaeaea;

            .action-buttons {
                display: flex;
                gap: 20px;
                justify-content: center;
                padding: 0 20px;

                .action-btn {
                    min-width: 140px;
                    padding: 10px 16px;
                    height: auto;

                    &:deep(.ant-btn-primary) {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                }
            }
        }
    }

    .reply-compose-form {
        flex-shrink: 0; // Bu alan sıkışmasın
        margin-top: 8px;
        border-top: 1px solid #eaeaea;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
        height: auto;

        :deep(.mail-compose) {
            box-shadow: none; // Varsayılan box-shadow'u kaldır
            border-radius: 8px;
            overflow: hidden;
            height: auto;
        }
    }
}
</style>

<style lang="scss">
.supsis-dialog {
    .dg-content-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;

        .dg-btn--cancel {
            color: #22272d;
            background-color: #fefefe;
            border-color: #22272d;
        }

        .dg-btn--ok {
            color: #fff;
            background-color: #22272d;
            border-color: #22272d;
        }
    }
}

.mail-details-popover {
    min-width: 250px;
    max-width: 350px;

    .detail-item {
        display: flex;
        margin-bottom: 8px;
        align-items: flex-start;

        &:last-child {
            margin-bottom: 0;
        }

        .detail-label {
            font-weight: 500;
            color: #555;
            width: 60px;
            flex-shrink: 0;
            padding-right: 8px;
        }

        .detail-value {
            flex: 1;
            color: #444;
            word-break: break-word;
        }
    }
}

.custom-mail-dropdown {
    .ant-dropdown-menu-item {
        display: flex;
        align-items: center;
        gap: 3px;
        cursor: pointer;
        transition: none;

        &:hover {
            background-color: #22272d;
            color: #fff;

            .material-icons {
                color: #fff;
            }
        }

        .ai-tool-menu-item {
            display: flex;
            align-items: center;
            width: 100%;
            padding: 2px 6px;
            border-radius: 4px;
            background-color: rgba(99, 102, 241, 0.1);
            position: relative;
            overflow: hidden;

            i {
                font-size: 18px;
                margin-right: 8px;
                color: #6366f1;
                position: relative;
                z-index: 1;
            }

            span {
                color: #4338ca;
                font-weight: 500;
                position: relative;
                z-index: 1;
            }
        }

        &:hover {
            .ai-tool-menu-item {
                background-color: rgba(255, 255, 255, 0.1);

                i,
                span {
                    color: white;
                }
            }
        }

        &.ai-menu-item {
            position: relative;
            overflow: hidden;
            background-color: rgba(99, 102, 241, 0.07);

            &::before {
                content: "";
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.2), transparent);
                animation: shineEffect 3s infinite;
            }

            &:hover {
                background-color: #22272d;

                &::before {
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    animation: shineEffect 1.5s infinite;
                }
            }
        }
    }
}

@keyframes shineEffect {
    0% {
        left: -100%;
    }

    30% {
        left: 100%;
    }

    100% {
        left: 100%;
    }
}
</style>
